import { CalendarDate, parseDate } from '@internationalized/date'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/sl'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

export default dayjs

export const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * Times are shown in the business's timezone, not the guest's: the guest walks in at the
 * time on the door, whatever their phone is set to.
 */
let zone = 'Europe/Ljubljana'

export const setZone = (tz: string | null | undefined) => {
    if (tz) zone = tz
}

export const inZone = (value: string) => dayjs(value).tz(zone)

export const formatTime = (value?: string | null): string =>
    value ? inZone(value).format('HH:mm') : ''

/** "sreda, 1. 10. 2026" / "Wednesday, 1 Oct 2026" in the current locale. */
export const formatLongDate = (value: string | null | undefined, locale: string): string =>
    value ? new Intl.DateTimeFormat(locale === 'sl' ? 'sl-SI' : 'en-GB', {
        weekday:  'long',
        day:      'numeric',
        month:    'long',
        year:     'numeric',
        timeZone: zone,
    }).format(new Date(value)) : ''

/** A YYYY-MM-DD calendar day, long form, in the current locale. */
export const formatLongDay = (value: string, locale: string): string =>
    new Intl.DateTimeFormat(locale === 'sl' ? 'sl-SI' : 'en-GB', {
        weekday: 'long',
        day:     'numeric',
        month:   'long',
        timeZone: 'UTC',
    }).format(new Date(`${value}T12:00:00Z`))

export const todayInZone = (): string => dayjs().tz(zone).format(DATE_FORMAT)

export const shiftDay = (value: string, days: number): string =>
    dayjs(value, DATE_FORMAT).add(days, 'day').format(DATE_FORMAT)

export const toCalendarDate = (value: string): CalendarDate => parseDate(value)

/**
 * The span an entry code opens a gate in, said once: "26. 9. 2026 od 08:00 do 08:30"
 * when it is one day, both dates only when it runs past midnight.
 */
export const accessWindow = (from: string, until: string, locale: string): { sameDay: boolean, day: string, from: string, until: string } => {
    const start = inZone(from)
    const end   = inZone(until)
    const date  = (value: ReturnType<typeof inZone>) => value.format(locale === 'sl' ? 'D. M. YYYY' : 'D/M/YYYY')
    const sameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')

    return sameDay
        ? { sameDay, day: date(start), from: start.format('HH:mm'), until: end.format('HH:mm') }
        : { sameDay, day: '', from: `${date(start)} ${start.format('HH:mm')}`, until: `${date(end)} ${end.format('HH:mm')}` }
}
